import { css } from '@emotion/css';

import {
    generateState,
    localState,
    selectState,
    Updater,
    useStore,
} from 'dg-web-shared/lib/Flux';
import { InputContext } from 'dg-web-shared/tb-ui/inputs/InputContext.ts';
import { SlideInPortalId } from '../../account/root/components/PortalSlidein';
import {
    FullWidthBottomButton,
    FullWidthBottomButtonColor,
} from '../../common/components/FullWidthBottomButton';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import * as LayoutState from '../../layout/state/LayoutState';
import { Login } from '../../login/components/Login';
import {
    CityDropinContainerState,
    CityDropinList,
    selectCityDropinState,
} from '../../park-create/components/park-option/CityDropin';
import { CitiesState } from '../../park-create/components/ParkOptionList';
import * as CityDropinState from '../../park-create/state/CityDropinState';
import { Colors } from 'dg-web-shared/ui/vars';
import { logAction } from '../../utils/ActionLog';
import { HasEverLoggedInState } from './HasEverLoggedInState';
import { LandingPageFAQ } from './LandingPageFAQ';
import { LandingPageFooter } from './LandingPageFooter';
import {
    DesktopLandingPageHeader,
    MobileLandingPageHeader,
} from './LandingPageHeader';
import { LandingPageHowItWorks } from './LandingPageHowItWorks';
import {
    BlueBackdrop,
    BlueBackdropHeader,
    ButtonType,
    LoadAppOrSignUp,
} from './LandingPageShared';
import {
    DesktopBreakpointWidth,
    FooterHeight,
    HeaderHeight,
    screenHeightPercentile,
} from './LandingPageVars';
import { portalOrModalSlideIn } from './PortalSlideInOrModal';
import { useState } from 'react';
import { isAndroid, isIos } from 'dg-web-shared/common/utils/BrowserOrigin';
import { isRunningInNativeWrapper } from '../../Native';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import moodImage from './assets/parking-emotion.jpg';
import { SingleSelection } from '../../common/components/SingleSelection.tsx';
import { Signup } from '../../signup/components/Signup.tsx';

interface State {
    scroll: ScrollState.State;
    layout: LayoutState.State;
    hasEverLoggedIn: HasEverLoggedInState.State;
}

export function LandingPage() {
    const { storeState, update } = useStore<State>(store => ({
        scroll: ScrollState.get(store),
        layout: new LayoutState.StateSlice(store).state,
        hasEverLoggedIn: HasEverLoggedInState.get(store),
    }));

    const [signupOpen, setSignupOpen] = useState(false);

    return (
        <div
            className={css({
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                overflow: 'hidden',
                backround: 'white',
            })}
        >
            <div
                className={css({
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                })}
            >
                {storeState.layout.viewportWidth < DesktopBreakpointWidth && (
                    <MobileLandingPageHeader
                        bigLoginPossible={isRunningInNativeWrapper()}
                        viewportHeight={storeState.layout.viewportHeight}
                        scrollTop={storeState.scroll.top}
                        everScrolled={storeState.scroll.everScrolled}
                        hasEverLoggedIn={storeState.hasEverLoggedIn.hasEver}
                    />
                )}
                <div
                    className={css({
                        flexGrow: 1,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        WebkitOverflowScrolling: 'touch',
                    })}
                    onScroll={e => {
                        const el = e.target;
                        const scrollTop = Math.max(
                            (el as HTMLElement).scrollTop,
                            0,
                        );
                        update(s => {
                            const scrolledFarEnough =
                                screenHeightPercentile(
                                    storeState.layout.viewportHeight,
                                    1 / 3,
                                ) < scrollTop;
                            if (
                                !storeState.scroll.everScrolled &&
                                scrolledFarEnough
                            ) {
                                logAction(s, 'landing-page-scrolled-down');
                            }
                            return ScrollState.stateWrite(s, {
                                everScrolled:
                                    storeState.scroll.everScrolled ||
                                    scrolledFarEnough,
                                top: scrollTop,
                            });
                        });
                    }}
                >
                    <div>
                        {storeState.hasEverLoggedIn.hasEver ? (
                            <div
                                className={css({
                                    paddingTop: '1px',
                                    background: Colors.white,
                                    height:
                                        storeState.layout.viewportHeight -
                                        FooterHeight -
                                        (storeState.layout.viewportWidth <
                                        DesktopBreakpointWidth
                                            ? HeaderHeight
                                            : 0),
                                })}
                            >
                                {!signupOpen && (
                                    <LoginContent
                                        openSignup={() => setSignupOpen(true)}
                                    />
                                )}
                            </div>
                        ) : (
                            <Content
                                viewportHeight={
                                    storeState.layout.viewportHeight
                                }
                                viewportWidth={storeState.layout.viewportWidth}
                                update={update}
                                isInApp={isRunningInNativeWrapper()}
                                openSignup={() => setSignupOpen(true)}
                            />
                        )}
                    </div>
                    <LandingPageFooter />
                </div>
                <SignUpContent
                    portal={SlideInPortalId.UNAUTH_LANDING_PAGE}
                    onClose={() => setSignupOpen(false)}
                    viewportWidth={storeState.layout.viewportWidth}
                    open={signupOpen}
                    hasEverLoggedIn={storeState.hasEverLoggedIn.hasEver}
                    title={
                        <Localized
                            de="Kontoeröffnung"
                            fr="Ouverture de compte"
                            it="Apertura conto"
                            en="Sign up"
                        />
                    }
                />
                {storeState.layout.viewportWidth > DesktopBreakpointWidth && (
                    <DesktopLandingPageHeader
                        hasEverLoggedIn={storeState.hasEverLoggedIn.hasEver}
                        update={update}
                    />
                )}
                <div
                    id={SlideInPortalId.UNAUTH_LANDING_PAGE}
                    className={css({ overflow: 'hidden' })}
                />
            </div>
        </div>
    );
}

const SignUpContent = portalOrModalSlideIn<{
    hasEverLoggedIn: boolean;
}>(p => {
    return <Signup showLoginHint={p.hasEverLoggedIn} close={p.onClose} />;
});

function LoginContent({ openSignup }: { openSignup: () => void }) {
    const { storeState, update } = useStore(store => ({
        layout: new LayoutState.StateSlice(store).state,
    }));

    const [forgotPasswordVisible, setForgotPasswordVisible] = useState(false);

    const [email, setEmail] = useState('');

    return (
        <div
            className={css({
                marginTop: '24px',
                display: 'flex',
                flexDirection: 'column',
                [`@media(min-width: ${DesktopBreakpointWidth}px)`]: {
                    marginTop: '150px',
                },
            })}
        >
            <div
                className={css({
                    width: Math.min(storeState.layout.viewportWidth, 500),
                    marginLeft: 'auto',
                    marginRight: 'auto',
                })}
            >
                <Login
                    openForgotPassword={() => setForgotPasswordVisible(true)}
                    closeForgotPassword={() => setForgotPasswordVisible(false)}
                    forgotPasswordVisible={forgotPasswordVisible}
                    email={email}
                    setEmail={setEmail}
                />

                {!forgotPasswordVisible ? (
                    <div
                        className={css({
                            marginTop: '50px',
                            padding: '0 72px',
                            textAlign: 'center',
                            ...Typo.robotoRegular,
                            fontSize: '15px',
                            color: Colors.blue,
                            fontWeight: 500,
                        })}
                    >
                        <Localized
                            de="Noch kein Konto?"
                            fr="Pas encore un compte?"
                            it="Non avete ancora un conto?"
                            en="You don't have an account yet?"
                        />
                        <FullWidthBottomButton
                            color={FullWidthBottomButtonColor.WHITE}
                            onClick={() => {
                                openSignup();
                                update(s =>
                                    logAction(s, 'landing-page-click-sign-up'),
                                );
                            }}
                            label={{
                                de: 'Hier registrieren',
                                fr: 'Enregistrez-vous ici',
                                it: 'Registratevi qui',
                                en: 'Sign up here',
                            }}
                        />
                    </div>
                ) : (
                    ''
                )}
            </div>
        </div>
    );
}

export namespace ScrollState {
    export interface State {
        top: number;
        everScrolled: boolean;
    }

    export const { get, stateWrite } = generateState<State>(
        'landing-page-scroll-pos',
        {
            top: 0,
            everScrolled: false,
        },
    );
}

export const Content = ({
    viewportHeight,
    viewportWidth,
    update,
    isInApp,
    openSignup,
}: {
    viewportHeight: number;
    viewportWidth: number;
    update: Updater;
    isInApp: boolean;
    openSignup: () => void;
}) => {
    const android = isAndroid();
    const iOs = isIos();
    const showLoadApp = !isInApp && (android || iOs);
    const isDesktop = viewportWidth > DesktopBreakpointWidth;
    return (
        <>
            <div
                className={css({
                    height:
                        screenHeightPercentile(
                            viewportHeight,
                            isDesktop ? 1 / 2.2 : 1 / 3,
                        ) - 20,
                    overflow: 'hidden',
                    marginLeft: isDesktop ? '0' : '-25%',
                })}
            >
                <img
                    src={moodImage}
                    width={isDesktop ? '100%' : '150%'}
                    className={css({
                        marginTop: isDesktop ? '-10%' : '-17%',
                    })}
                />
            </div>
            <div
                className={css({
                    position: 'relative',
                    padding: '0 20px',
                    height:
                        screenHeightPercentile(
                            viewportHeight,
                            isDesktop ? 1 / 2.5 : 1 / 3,
                        ) - 5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-evenly',
                    background: Colors.white,
                })}
            >
                <PromotionButton
                    viewportWidth={viewportWidth}
                    showLoadApp={showLoadApp}
                    update={update}
                />

                <Claim>
                    <Localized
                        de={
                            <span>
                                Grösste Abdeckung <br /> in der Schweiz.
                            </span>
                        }
                        fr={
                            <span>
                                La plus grande couverture <br /> en Suisse.
                            </span>
                        }
                        it={
                            <span>
                                La maggiore diffusione <br /> in Svizzera.
                            </span>
                        }
                        en={
                            <span>
                                Largest coverage <br /> in Switzerland.
                            </span>
                        }
                    />
                </Claim>
                <Claim>
                    <Localized
                        de={
                            <span>
                                Einziger Anbieter <br /> auch für Parkhäuser.
                            </span>
                        }
                        fr={
                            <span>
                                Le seule aussi pour
                                <br />
                                les parking à barrières.
                            </span>
                        }
                        it={
                            <span>
                                L&#39;unica soluzione
                                <br />
                                anche per gli autosili.
                            </span>
                        }
                        en={
                            <span>
                                The only one also
                                <br />
                                for park garages.
                            </span>
                        }
                    />
                </Claim>
            </div>
            <BlueBackdrop image={'relief'}>
                <BlueBackdropHeader>
                    <Localized
                        de="Verfügbarkeit prüfen"
                        fr="Vérifier la disponibilité"
                        it="Verifica disponibilità"
                        en="Check availability"
                    />
                </BlueBackdropHeader>
                <CityList viewportWidth={viewportWidth} />
                <br />
                <LoadAppOrSignUp
                    update={update}
                    showLoadApp={showLoadApp}
                    type={ButtonType.white}
                    openSignup={openSignup}
                />
            </BlueBackdrop>
            <LandingPageHowItWorks
                viewportWidth={viewportWidth}
                update={update}
                showLoadApp={showLoadApp}
                openSignup={openSignup}
            />
            <LandingPageFAQ
                update={update}
                showLoadApp={showLoadApp}
                openSignup={openSignup}
            />
        </>
    );
};

interface CityListState {
    cities: CitiesState.State;
}

const CityList = selectState<
    { viewportWidth: number },
    CityListState & CityDropinContainerState
>(
    s => {
        const dropinState = selectCityDropinState(s);
        const location = dropinState.geolocation;
        return {
            cities: CitiesState.get(s, {
                location: location.currentLocation,
                isAuthed: false,
            }),
            ...dropinState,
        };
    },
    p => {
        const cities = p.cities.data;
        return (
            <div>
                <SingleSelection
                    labelText=""
                    selection={
                        cities ? (
                            <Localized
                                de={`${cities.length} Standorte`}
                                fr={`${cities.length} sites`}
                                it={`${cities.length} ubicazioni`}
                                en={`${cities.length} locations`}
                            />
                        ) : (
                            <Localized
                                de={'PLZ / Ort'}
                                fr={`NPA / Localité`}
                                it={`NAP / Località`}
                                en={`ZIP / City`}
                            />
                        )
                    }
                    onClick={() => {
                        p.update(s => {
                            new CityDropinState.StateSlice(s).setSortField(
                                CityDropinState.SortField.Distance,
                            );
                            new CityDropinState.StateSlice(s).setSortAscending(
                                true,
                            );
                            new CityDropinState.StateSlice(s).setOpen(true);

                            logAction(s, 'landing-page-open-city-dropin');
                            return 'open-unauth-city-dropin';
                        });
                    }}
                    context={InputContext.inverted}
                />
                {cities && (
                    <CityListSlideIn
                        open={p.cityDropin.open}
                        title={
                            <Localized
                                de={`${cities.length} Standorte`}
                                fr={`${cities.length} sites`}
                                it={`${cities.length} ubicazioni`}
                                en={`${cities.length} locations`}
                            />
                        }
                        onClose={() => {
                            p.update(s => {
                                new CityDropinState.StateSlice(s).setOpen(
                                    false,
                                );
                                return 'close-unauth-city-dropin';
                            });
                        }}
                        citiesData={cities}
                        {...p}
                        portal={SlideInPortalId.UNAUTH_LANDING_PAGE}
                    />
                )}
            </div>
        );
    },
);

interface CityListSlideInProps {
    update: Updater;
    citiesData: Readonly<CitiesState.City[]>;
}

const CityListSlideIn = portalOrModalSlideIn<
    CityDropinContainerState & CityListSlideInProps
>(p => {
    return <CityDropinList {...p} cities={p.citiesData} noZones={true} />;
});

const Claim = (p: { children?: React.ReactNode }) => (
    <h1
        className={css({
            display: 'block',
            fontSize: '28px',
            margin: 0,
            '@media(max-height: 600px)': {
                fontSize: '24px',
            },
            '@media(max-height: 480px)': {
                fontSize: '22px',
            },
            [`@media(min-width: ${DesktopBreakpointWidth}px)`]: {
                fontSize: '50px',
                lineHeight: '1.1',
                fontWeigth: 900,
            },
            ...Typo.robotoUltraBold,
            color: Colors.darkblue,
        })}
    >
        <div
            className={css({
                maxWidth: '500px',
                marginLeft: 'auto',
                marginRight: 'auto',
            })}
        >
            {p.children}
        </div>
    </h1>
);

interface PromotionButtonState {
    open: boolean;
}

const PromotionButton = localState<
    {
        update: Updater;
        viewportWidth: number;
        showLoadApp: boolean;
    },
    PromotionButtonState
>({ open: false }, p => {
    const isMobile = p.viewportWidth < DesktopBreakpointWidth;

    const contentMargin = (p.viewportWidth - 500) / 2;
    return (
        <>
            <div
                className={css({
                    position: 'absolute',
                    top: -70,
                    right: isMobile
                        ? 20
                        : Math.max(contentMargin - 200 - 100, 48),
                    width: isMobile ? '98px' : '200px',
                    height: isMobile ? '98px' : '200px',
                    borderRadius: '50%',
                    transform: 'rotate(-10deg)',
                    textAlign: 'center',
                    fontSize: isMobile ? '13px' : '24px',
                    paddingTop: isMobile ? '22px' : '40px',
                    paddingLeft: isMobile ? '2px' : '4px',
                    ...Typo.robotoMedium,
                    fontStyle: 'italic',
                    color: Colors.white,
                    letterSpacing: isMobile ? '1.2px' : '2.3px',
                    background: 'linear-gradient(225deg, #9e160e, #ff002f)',
                })}
            >
                <Localized
                    de="an über"
                    fr="dans plus"
                    it="in più di"
                    en="at over"
                />{' '}
                <br />{' '}
                <span
                    className={css({
                        fontSize: isMobile ? '17px' : '36px',
                        padding: '4px 0',
                    })}
                >
                    {' '}
                    <Localized de="1150" fr="1150" it="1150" en="1150" />
                </span>
                <br />
                <span>
                    <Localized
                        de="Standorten"
                        fr="sites"
                        it="località"
                        en="locations"
                    />{' '}
                </span>
            </div>
        </>
    );
});
